// src/components/ui/ResumeForm.js
import React, { useState, useEffect } from 'react';
import { Plus, Minus } from 'lucide-react';
import FancySelect from './FancySelect';

const dedupe = (arr = []) => Array.from(new Set(arr));

const normalizePhone = raw => {
    const digits = (raw || '').replace(/\D/g, '').slice(0, 11);
    const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (d.length < 4) return d;
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
};

// Enhanced bullet character regex - includes more variants and box characters
const BULLET_CHARS = /[\u2022\u2023\u25E6\u2043\u2219\u25AA\u25AB\u25CF\u25CB\u25A0\u25A1\u2610\u2611\u2612\u2713\u2714\u2715\u2716\u2717\u2718]/g;

// Box drawing characters and other formatting artifacts
const BOX_CHARS = /[\u2500-\u257F\u2580-\u259F\[\]\{\}\(\)]/g;

const cleanPastedText = (raw, options = {}) => {
    const { keepNewlines = false, normalizeCase = false } = options;
    let text = raw || '';

    // Remove bullet points and box drawing characters
    text = text.replace(BULLET_CHARS, '');
    text = text.replace(BOX_CHARS, '');

    // Replace non-breaking spaces with regular spaces
    text = text.replace(/\u00A0/g, ' ');

    // Normalize line endings
    text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // Remove multiple spaces that might appear after removing bullets
    text = text.replace(/\s{2,}/g, ' ');

    if (keepNewlines) {
        const lines = text
            .split('\n')
            .map(line => line.replace(/\s+/g, ' ').trim())
            .filter(line => line.length > 0);
        text = lines.join('\n');
    } else {
        // Replace newlines with spaces for single-line fields
        text = text.replace(/\n/g, ' ');
        text = text.replace(/\s+/g, ' ');
    }

    text = text.trim();

    // Normalize case for name fields (Title Case instead of ALL CAPS)
    if (normalizeCase && text === text.toUpperCase() && text.length > 0) {
        text = text.toLowerCase().split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    }

    return text;
};

const handlePlainTextPaste = (e, currentValue, onValueChange, options = {}) => {
    e.preventDefault();
    const clipboardText =
        e.clipboardData.getData('text/plain') ||
        e.clipboardData.getData('text') ||
        '';
    const cleaned = cleanPastedText(clipboardText, options);
    const target = e.target;
    const start = target.selectionStart ?? currentValue.length;
    const end = target.selectionEnd ?? start;
    const before = currentValue.slice(0, start);
    const after = currentValue.slice(end);
    const newValue = before + cleaned + after;
    onValueChange(newValue);
    window.requestAnimationFrame(() => {
        try {
            const pos = start + cleaned.length;
            target.setSelectionRange?.(pos, pos);
        } catch { }
    });
};

// Helper for skills parsing
const parseSkills = text =>
    dedupe(
        (text || '')
            .split(/[,\n;]/)
            .map(s => s.trim())
            .filter(Boolean)
    );

function ResumeForm({ data, onChange, onSave, isSaving = false }) {
    const setField = (k, v) => onChange({ ...data, [k]: v });

    // ---------- local state JUST for the skills input ----------
    const [skillsText, setSkillsText] = useState((data.skills || []).join(', '));

    // keep in sync if skills array changes from outside (e.g. loaded profile)
    useEffect(() => {
        setSkillsText((data.skills || []).join(', '));
    }, [data.skills]);
    // -----------------------------------------------------------

    const addExp = () =>
        onChange({
            ...data,
            experience: [
                ...data.experience,
                { title: '', company: '', startDate: '', endDate: '', description: '' },
            ],
        });

    const rmExp = i =>
        onChange({
            ...data,
            experience: data.experience.filter((_, idx) => idx !== i),
        });

    const upExp = (i, k, v) => {
        const copy = [...data.experience];
        copy[i] = { ...copy[i], [k]: v };
        onChange({ ...data, experience: copy });
    };

    const addEdu = () =>
        onChange({
            ...data,
            education: [
                ...data.education,
                { degree: '', fieldOfStudy: '', school: '', graduationDate: '' },
            ],
        });

    const rmEdu = i =>
        onChange({
            ...data,
            education: data.education.filter((_, idx) => idx !== i),
        });

    const upEdu = (i, k, v) => {
        const copy = [...data.education];
        copy[i] = { ...copy[i], [k]: v };
        onChange({ ...data, education: copy });
    };

    // only convert to array when we explicitly call this
    const onSkills = val => {
        const skillsArray = parseSkills(val);
        setField('skills', skillsArray);
    };

    // === OPTION LISTS ===
    const disabilityOptions = [
        { value: '', label: 'Select...' },
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
        { value: 'Decline To Self Identify', label: 'Decline To Self Identify' },
    ];

    const veteranOptions = [
        { value: '', label: 'Select...' },
        { value: 'I am a veteran', label: 'I am a veteran' },
        { value: 'I am not a veteran', label: 'I am not a veteran' },
        { value: 'Decline To Self Identify', label: 'Decline To Self Identify' },
    ];

    const raceOptions = [
        { value: '', label: 'Select...' },
        { value: 'American Indian or Alaska Native', label: 'American Indian or Alaska Native' },
        { value: 'Asian', label: 'Asian' },
        { value: 'Black or African American', label: 'Black or African American' },
        { value: 'Hispanic or Latino', label: 'Hispanic or Latino' },
        { value: 'Middle Eastern or North African', label: 'Middle Eastern or North African' },
        {
            value: 'Native Hawaiian or Other Pacific Islander',
            label: 'Native Hawaiian or Other Pacific Islander',
        },
        { value: 'White', label: 'White' },
        { value: 'Two or More Races', label: 'Two or More Races' },
        { value: 'Other', label: 'Other' },
        { value: 'Prefer not to say', label: 'Prefer not to say' },
    ];

    const genderOptions = [
        { value: '', label: 'Select...' },
        { value: 'Male', label: 'Male' },
        { value: 'Female', label: 'Female' },
        { value: 'Other', label: 'Other' },
        { value: 'Decline To Self Identify', label: 'Decline To Self Identify' },
    ];

    const sponsorshipOptions = [
        { value: '', label: 'Select...' },
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];

    const authorizationOptions = [
        { value: '', label: 'Select...' },
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];

    // Simple input class - NO Tailwind border/focus classes
    const inputClass = 'form-input';
    const textareaClass = 'form-textarea';

    return (
        <div
            className="animate-fade-in-up"
            style={{ animationDelay: '0.4s', opacity: 0 }}
        >
            {/* Basic Info */}
            <div className="mb-8">
                <h3 className="text-2xl font-bold text-[var(--charcoal-black)] mb-6 font-poppins">
                    Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            First Name *
                        </label>
                        <input
                            className={inputClass}
                            value={data.firstName}
                            onChange={e => setField('firstName', e.target.value)}
                            onPaste={e =>
                                handlePlainTextPaste(e, data.firstName, v =>
                                    setField('firstName', v),
                                    { normalizeCase: true }
                                )
                            }
                        />
                    </div>
                    <div>
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            Last Name *
                        </label>
                        <input
                            className={inputClass}
                            value={data.lastName}
                            onChange={e => setField('lastName', e.target.value)}
                            onPaste={e =>
                                handlePlainTextPaste(e, data.lastName, v =>
                                    setField('lastName', v),
                                    { normalizeCase: true }
                                )
                            }
                        />
                    </div>
                    <div>
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            Email *
                        </label>
                        <input
                            className={inputClass}
                            type="email"
                            value={data.email}
                            onChange={e => setField('email', e.target.value)}
                            onPaste={e =>
                                handlePlainTextPaste(e, data.email, v =>
                                    setField('email', v),
                                    { normalizeCase: true }
                                )
                            }
                        />
                    </div>
                    <div>
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            Phone
                        </label>
                        <input
                            className={inputClass}
                            type="tel"
                            value={data.phone}
                            onChange={e => setField('phone', e.target.value)}
                            onBlur={e =>
                                setField('phone', normalizePhone(e.target.value))
                            }
                            onPaste={e =>
                                handlePlainTextPaste(e, data.phone, v =>
                                    setField('phone', v)
                                )
                            }
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            Address
                        </label>
                        <input
                            className={inputClass}
                            value={data.address}
                            onChange={e => setField('address', e.target.value)}
                            onPaste={e =>
                                handlePlainTextPaste(e, data.address, v =>
                                    setField('address', v)
                                )
                            }
                        />
                    </div>
                    <div>
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            City
                        </label>
                        <input
                            className={inputClass}
                            value={data.city}
                            onChange={e => setField('city', e.target.value)}
                            onPaste={e =>
                                handlePlainTextPaste(e, data.city, v =>
                                    setField('city', v)
                                )
                            }
                        />
                    </div>
                    <div>
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            State
                        </label>
                        <input
                            className={inputClass}
                            value={data.state}
                            onChange={e => setField('state', e.target.value)}
                            onPaste={e =>
                                handlePlainTextPaste(e, data.state, v =>
                                    setField('state', v)
                                )
                            }
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            LinkedIn URL
                        </label>
                        <input
                            className={inputClass}
                            type="url"
                            value={data.linkedinUrl || ''}
                            onChange={e => setField('linkedinUrl', e.target.value)}
                            onPaste={e =>
                                handlePlainTextPaste(e, data.linkedinUrl || '', v =>
                                    setField('linkedinUrl', v)
                                )
                            }
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            Professional Summary
                        </label>
                        <textarea
                            className={textareaClass}
                            rows={4}
                            value={data.professionalSummary || ''}
                            onChange={e => setField('professionalSummary', e.target.value)}
                            onPaste={e =>
                                handlePlainTextPaste(
                                    e,
                                    data.professionalSummary || '',
                                    v => setField('professionalSummary', v),
                                    { keepNewlines: false }
                                )
                            }
                        />
                    </div>
                </div>
            </div>

            {/* Additional Info */}
            <div className="mb-8">
                <h3 className="text-2xl font-bold text-[var(--charcoal-black)] mb-6 font-poppins">
                    Additional Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            Disability Status
                        </label>
                        <FancySelect
                            value={data.disabilityStatus}
                            onChange={v => setField('disabilityStatus', v)}
                            options={disabilityOptions}
                        />
                    </div>
                    <div>
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            Veteran Status
                        </label>
                        <FancySelect
                            value={data.veteranStatus}
                            onChange={v => setField('veteranStatus', v)}
                            options={veteranOptions}
                        />
                    </div>
                    <div>
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            Race / Ethnicity
                        </label>
                        <FancySelect
                            value={data.raceEthnicity}
                            onChange={v => setField('raceEthnicity', v)}
                            options={raceOptions}
                        />
                    </div>
                    <div>
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            Gender
                        </label>
                        <FancySelect
                            value={data.gender}
                            onChange={v => setField('gender', v)}
                            options={genderOptions}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            Do you now OR will you (in the future) require visa
                            sponsorship?
                        </label>
                        <FancySelect
                            value={data.sponsorshipRequired}
                            onChange={v => setField('sponsorshipRequired', v)}
                            options={sponsorshipOptions}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block font-medium mb-2 text-[var(--charcoal-black)]">
                            Are you currently authorized to work in the country?
                        </label>
                        <FancySelect
                            value={data.workAuthorization}
                            onChange={v => setField('workAuthorization', v)}
                            options={authorizationOptions}
                        />
                    </div>
                </div>
            </div>

            {/* Skills */}
            <div className="mb-8">
                <h3 className="text-2xl font-bold text-[var(--charcoal-black)] mb-4 font-poppins">
                    Skills
                </h3>
                <input
                    className={inputClass}
                    placeholder="e.g., Java, Python, Project Management"
                    value={skillsText}
                    onChange={e => {
                        setSkillsText(e.target.value);
                    }}
                    onBlur={() => {
                        onSkills(skillsText);
                    }}
                    onPaste={e =>
                        handlePlainTextPaste(
                            e,
                            skillsText,
                            v => setSkillsText(v),
                            { keepNewlines: false }
                        )
                    }
                />
                <p className="text-sm text-[var(--forest-green)] mt-2">
                    Separate skills with commas
                </p>
            </div>

            {/* Work Experience */}
            <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold text-[var(--charcoal-black)] font-poppins">
                        Work Experience
                    </h3>
                    {/* navbar green + pill */}
                    <button
                        type="button"
                        onClick={addExp}
                        className="flex items-center gap-2 px-5 py-2.5 bg-[var(--forest-green)] text-white rounded-full hover:bg-[var(--deep-teal)] transition-colors shadow-md hover:shadow-lg"
                    >
                        <Plus className="w-4 h-4" /> Add Experience
                    </button>
                </div>
                {data.experience.map((exp, i) => (
                    <div
                        key={i}
                        className="mb-6 p-6 bg-[var(--off-white)] rounded-xl border-2 border-[var(--off-white)]"
                    >
                        <div className="flex justify-between items-start mb-4">
                            <h4 className="font-semibold text-[var(--charcoal-black)]">
                                Position {i + 1}
                            </h4>
                            {data.experience.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => rmExp(i)}
                                    className="text-[var(--coral-red)] hover:bg-white p-2 rounded transition-colors"
                                >
                                    <Minus className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                className={inputClass}
                                placeholder="Job Title"
                                value={exp.title}
                                onChange={e =>
                                    upExp(i, 'title', e.target.value)
                                }
                                onPaste={e =>
                                    handlePlainTextPaste(
                                        e,
                                        exp.title,
                                        v => upExp(i, 'title', v)
                                    )
                                }
                            />
                            <input
                                className={inputClass}
                                placeholder="Company Name"
                                value={exp.company}
                                onChange={e =>
                                    upExp(i, 'company', e.target.value)
                                }
                                onPaste={e =>
                                    handlePlainTextPaste(
                                        e,
                                        exp.company,
                                        v => upExp(i, 'company', v)
                                    )
                                }
                            />
                            <input
                                className={inputClass}
                                placeholder="Start Date (YYYY-MM)"
                                value={exp.startDate}
                                onChange={e =>
                                    upExp(i, 'startDate', e.target.value)
                                }
                                onPaste={e =>
                                    handlePlainTextPaste(
                                        e,
                                        exp.startDate,
                                        v => upExp(i, 'startDate', v)
                                    )
                                }
                            />
                            <input
                                className={inputClass}
                                placeholder="End Date (or 'Present')"
                                value={exp.endDate}
                                onChange={e =>
                                    upExp(i, 'endDate', e.target.value)
                                }
                                onPaste={e =>
                                    handlePlainTextPaste(
                                        e,
                                        exp.endDate,
                                        v => upExp(i, 'endDate', v)
                                    )
                                }
                            />
                            <textarea
                                className={`${textareaClass} md:col-span-2`}
                                rows={3}
                                placeholder="Responsibilities / achievements"
                                value={exp.description}
                                onChange={e =>
                                    upExp(i, 'description', e.target.value)
                                }
                                onPaste={e =>
                                    handlePlainTextPaste(
                                        e,
                                        exp.description,
                                        v =>
                                            upExp(
                                                i,
                                                'description',
                                                v
                                            ),
                                        { keepNewlines: false }
                                    )
                                }
                            />
                        </div>
                    </div>
                ))}
            </div>

            {/* Education */}
            <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold text-[var(--charcoal-black)] font-poppins">
                        Education
                    </h3>
                    {/* navbar green + pill */}
                    <button
                        type="button"
                        onClick={addEdu}
                        className="flex items-center gap-2 px-5 py-2.5 bg-[var(--forest-green)] text-white rounded-full hover:bg-[var(--deep-teal)] transition-colors shadow-md hover:shadow-lg"
                    >
                        <Plus className="w-4 h-4" /> Add Education
                    </button>
                </div>
                {data.education.map((edu, i) => (
                    <div
                        key={i}
                        className="mb-6 p-6 bg-[var(--off-white)] rounded-xl border-2 border-[var(--off-white)]"
                    >
                        <div className="flex justify-between items-start mb-4">
                            <h4 className="font-semibold text-[var(--charcoal-black)]">
                                Education {i + 1}
                            </h4>
                            {data.education.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => rmEdu(i)}
                                    className="text-[var(--coral-red)] hover:bg-white p-2 rounded transition-colors"
                                >
                                    <Minus className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                className={inputClass}
                                placeholder="Degree"
                                value={edu.degree}
                                onChange={e =>
                                    upEdu(i, 'degree', e.target.value)
                                }
                                onPaste={e =>
                                    handlePlainTextPaste(
                                        e,
                                        edu.degree,
                                        v => upEdu(i, 'degree', v)
                                    )
                                }
                            />
                            <input
                                className={inputClass}
                                placeholder="Field of Study"
                                value={edu.fieldOfStudy}
                                onChange={e =>
                                    upEdu(i, 'fieldOfStudy', e.target.value)
                                }
                                onPaste={e =>
                                    handlePlainTextPaste(
                                        e,
                                        edu.fieldOfStudy,
                                        v =>
                                            upEdu(
                                                i,
                                                'fieldOfStudy',
                                                v
                                            )
                                    )
                                }
                            />
                            <input
                                className={inputClass}
                                placeholder="School"
                                value={edu.school}
                                onChange={e =>
                                    upEdu(i, 'school', e.target.value)
                                }
                                onPaste={e =>
                                    handlePlainTextPaste(
                                        e,
                                        edu.school,
                                        v => upEdu(i, 'school', v)
                                    )
                                }
                            />
                            <input
                                className={inputClass}
                                placeholder="Graduation Date (YYYY-MM)"
                                value={edu.graduationDate}
                                onChange={e =>
                                    upEdu(
                                        i,
                                        'graduationDate',
                                        e.target.value
                                    )
                                }
                                onPaste={e =>
                                    handlePlainTextPaste(
                                        e,
                                        edu.graduationDate,
                                        v =>
                                            upEdu(
                                                i,
                                                'graduationDate',
                                                v
                                            )
                                    )
                                }
                            />
                        </div>
                    </div>
                ))}
            </div>

            {/* Save button ΓÇô pill like jobque.ai */}
            <button
                type="button"
                onClick={onSave}
                disabled={isSaving}
                className="w-full py-4 bg-[var(--forest-green)] text-white font-semibold text-lg rounded-full hover:bg-[var(--deep-teal)] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
                {isSaving ? 'Saving...' : 'Save Resume Profile'}
            </button>
        </div>
    );
}

export default ResumeForm;
